import { useCallback, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { UploadFile } from "antd";
import { AxiosError } from "axios";

import { IAward, IFederalLottery, IGiveawayAuto } from "../../../../../interfaces";

import { EmitError } from "../../../../../utils/EmitError";
import GiveawaysService from "../../../../../services/http/services/GiveawaysService";
import { EmitSuccessAlert } from "../../../../../utils/EmitSuccessAlert";
import LotteryFederalService from "../../../../../services/http/services/LotteryFederalService";
import { EGiveawayStatus } from "../../../../../enums";
import GiveawaysAutoService from "../../../../../services/http/services/GiveawaysAutoService";

const ACCEPTED_FILE_TYPES = [
    'application/json',
]

export interface IChangeFileProps {
    onChangeHookForm: (event: any) => void,
    file: UploadFile,
}

interface IInputs {
    participants_file: UploadFile;
    hash: string;
}

interface IProps {
    giveawayAuto: IGiveawayAuto | undefined;
    refreshGiveaway: () => void;
}

export default function useProcessData({ giveawayAuto, refreshGiveaway }: IProps) {
    const [isSubmiting, setIsSubmiting] = useState<boolean>(false);
    const [isDownloadingFile, setIsDownloadingFile] = useState<boolean>(false);
    const [hash, setHash] = useState<string | undefined | null>(giveawayAuto?.hash_with_participants);
    const [file, setFile] = useState<string | undefined | null>(giveawayAuto?.file_with_participants);
    const [federalLottery, setFederalLottery] = useState<IFederalLottery | undefined>();
    const [processedData, setProcessedData] = useState<boolean>(giveawayAuto?.status !== EGiveawayStatus.WAITING_RAFFLE);
    const [beingUploadedParticipantsFileName, setBeingUploadedParticipantsFileName] = useState<string | undefined>();
    const [selectedAward, setSelectedAward] = useState<IAward | undefined>();
    const [modalAwardVisible, setModalAwardVisible] = useState<boolean>(false);


    const {
        control,
        handleSubmit,
        formState: { errors },
        setError,
        clearErrors,
        watch
    } = useForm<IInputs>({
        shouldFocusError: false,
        defaultValues: {
            participants_file: undefined,
            hash: giveawayAuto?.hash_with_participants || '',
        }
    });

    const onClickViewWinner = useCallback((award: IAward) => {
        setSelectedAward(award);
        setModalAwardVisible(true);
    }, []);

    const handleCloseModalAwardWinner = useCallback(() => {
        setModalAwardVisible(false);
    }, []);

    const handleSubmitForm: SubmitHandler<IInputs> = useCallback(async (data) => {
        try {

            if (!giveawayAuto) {
                return;
            }

            setIsSubmiting(true);
            const formData = new FormData();

            if (data.participants_file?.originFileObj) {
                formData.append('file', data.participants_file.originFileObj);
            }

            await GiveawaysAutoService.uploadGiveawayFederalLotteryResult({ raffleId: giveawayAuto.id, formData });


            setProcessedData(true);
            setHash(hash);
            setFile(file);
            setIsSubmiting(false);
            refreshGiveaway();
            EmitSuccessAlert('Resultado processado com sucesso!');

        } catch (err) {
            EmitError(err);
            setIsSubmiting(false);
        }
    }, [giveawayAuto, refreshGiveaway, file, hash]);

    const dummyRequest = useCallback(({ onSuccess }: any) => {
        setTimeout(() => {
            onSuccess("ok");
        }, 0);
    }, []);

    const handleChangeParticipantsFile = useCallback(({ onChangeHookForm, file }: IChangeFileProps) => {
        if (file?.type && !ACCEPTED_FILE_TYPES.includes(file.type.toLowerCase())) {
            setError('participants_file', { message: 'Formato de arquivo inválido! O formato aceito é apenas JSON' });
            return;
        }

        clearErrors('participants_file');
        onChangeHookForm(file);
        setBeingUploadedParticipantsFileName(file.name);
    }, [setError, clearErrors]);

    const handleClickDownloadFile = useCallback(async () => {
        if (!file) {
            return;
        }

        const controller = new AbortController();

        try {
            setIsDownloadingFile(true);
            const responseFile = await GiveawaysService.downloadGiveawayTicketsFile({ fileName: file, signal: controller.signal });

            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(responseFile);
            link.download = file;
            link.click();
            window.URL.revokeObjectURL(link.href);
            setIsDownloadingFile(false);
        } catch (err) {
            if (err instanceof AxiosError && err.name === 'CanceledError') {
                return;
            }

            setIsDownloadingFile(false);
            EmitError(err, true, 'holdGiveawayExportData_ajksdkasdjasjdasd')
        }

        return () => {
            controller.abort();
        }
    }, [file]);

    useEffect(() => {
        const controller = new AbortController();

        async function loadFederalLottery() {
            try {
                if (giveawayAuto?.date) {
                    const federalLottery = await LotteryFederalService.getFederalLotteryByDate(giveawayAuto.date, controller.signal);
                    setFederalLottery(federalLottery);
                }
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }

                EmitError(err);
            }
        }

        loadFederalLottery();

        return () => {
            controller.abort();
        }
    }, [giveawayAuto]);

    return {
        control,
        handleSubmit,
        handleSubmitForm,
        errors,
        dummyRequest,
        handleChangeParticipantsFile,
        beingUploadedParticipantsFileName,
        handleClickDownloadFile,
        file,
        hash,
        isDownloadingFile,
        isSubmiting,
        ACCEPTED_FILE_TYPES,
        federalLottery,
        processedData,
        watch,
        selectedAward,
        modalAwardVisible,
        handleCloseModalAwardWinner,
        onClickViewWinner
    }
}
